import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { setUiVersion, UiVersion } from "../../uiVersion";
import { mapPathAcrossVersions } from "./versionPathMapping";
import { getRoute, getRoutesByIntegration } from "../../api/routes/routesApi";

// GUNDI-5642: the one place that performs a version switch, shared by the
// banner (new → legacy) and the hidden unlock modal (legacy → new) so the two
// directions cannot drift apart. New code, not a frozen import, so it may use
// the CURRENT api modules for the path translation.
export function useVersionSwitch() {
    const { keycloak } = useKeycloak();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [switching, setSwitching] = useState(false);

    const validToken = () => {
        const token = keycloak?.token;
        return token && typeof token === "string" && !keycloak.isTokenExpired() ? token : null;
    };

    const switchTo = async (target: UiVersion) => {
        setSwitching(true);
        const token = validToken();
        const destination = await mapPathAcrossVersions(pathname, target, {
            routeIdForProvider: async (providerId) => {
                if (!token) return null;
                const { results, ok } = await getRoutesByIntegration(token, "provider", providerId);
                // Ambiguous old links land on the first route of the provider
                // (the plan's default/first rule); a failed lookup is not "none".
                return ok && results.length ? results[0]?.id ?? null : null;
            },
            providerIdForRoute: async (routeId) => {
                if (!token) return null;
                try {
                    const route = await getRoute(token, routeId);
                    return route?.data_providers?.[0]?.id ?? null;
                } catch {
                    return null;
                }
            },
        });
        // Flip the flag first: the shell re-renders the other tree, and the
        // navigate below lands it on a URL in that tree's vocabulary.
        setUiVersion(target);
        navigate(destination, { replace: true });
        setSwitching(false);
    };

    return { switchTo, switching };
}
